import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Button,
	Card,
	Col,
	Divider,
	Form,
	Input,
	Row,
	Select,
	Skeleton,
	Space,
	Tag,
	Typography,
	notification,
} from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import BasePageLayout from '../layout/BasePageLayout';
import {
	useGetBuildingIntegrationSettingsQuery,
	useGetBuildingsWithPrinterSettingsQuery,
	useUpdateBuildingIntegrationSettingsMutation,
	useUpdateBuildingPrinterSettingsMutation,
	type BuildingIntegrationSettingsUpdate,
} from '../../../redux/api/location';
import ProcessCatalogCard from '../components/processCatalog/ProcessCatalogCard';
import BallooningSettingsCard from '../components/ballooning/BallooningSettingsCard';

const { Text, Paragraph } = Typography;

const CEDARBURG_NAME = 'Cedarburg';

const PRINTER_FIELDS = [
	{ key: 'printer_name_delivery', label: 'Delivery' },
	{ key: 'printer_name_kitting', label: 'Kitting' },
	{ key: 'printer_name_cutting', label: 'Cutting' },
	{ key: 'printer_name_received', label: 'Receiving' },
	{ key: 'printer_name_process_tags', label: 'Process Tags' },
] as const;

const PROCESS_TAGS_TOOLTIP =
	'CUPS printer for the finishing, handling and cleaning tags (Forms 68-70). Leave blank to print them on the kitting printer alongside the existing tags.';

const SERVICE_URL_FIELD = {
	key: 'printing_service_url',
	label: 'Printing Service URL',
} as const;

const SECRET_PLACEHOLDER = '••••••••';

const SettingsPage: React.FC = () => {
	const [printerForm] = Form.useForm();
	const [integrationsForm] = Form.useForm();

	const { data: buildings = [], isLoading: isBuildingsLoading } =
		useGetBuildingsWithPrinterSettingsQuery();
	const { data: integrations = [], isLoading: isIntegrationsLoading } =
		useGetBuildingIntegrationSettingsQuery();

	const [updatePrinterSettings, { isLoading: isSavingPrinter }] =
		useUpdateBuildingPrinterSettingsMutation();
	const [updateIntegrations, { isLoading: isSavingIntegrations }] =
		useUpdateBuildingIntegrationSettingsMutation();

	const [selectedBuildingId, setSelectedBuildingId] = useState<string | undefined>(undefined);

	const selectedBuilding = buildings.find((b) => b.id === selectedBuildingId);
	const selectedIntegration = useMemo(
		() => integrations.find((it) => it.building_id === selectedBuildingId),
		[integrations, selectedBuildingId]
	);

	useEffect(() => {
		if (buildings.length === 0 || selectedBuildingId !== undefined) return;
		const cedarburg = buildings.find((b) => b.name === CEDARBURG_NAME);
		setSelectedBuildingId(cedarburg?.id ?? buildings[0].id);
	}, [buildings, selectedBuildingId]);

	useEffect(() => {
		if (!selectedBuilding) return;
		printerForm.setFieldsValue({
			printing_service_url: selectedBuilding.printing_service_url ?? '',
			printer_name_delivery: selectedBuilding.printer_name_delivery ?? '',
			printer_name_kitting: selectedBuilding.printer_name_kitting ?? '',
			printer_name_cutting: selectedBuilding.printer_name_cutting ?? '',
			printer_name_received: selectedBuilding.printer_name_received ?? '',
			printer_name_process_tags:
				selectedBuilding.printer_name_process_tags ?? '',
		});
	}, [selectedBuilding, printerForm]);

	useEffect(() => {
		if (!selectedIntegration) {
			integrationsForm.resetFields();
			return;
		}
		integrationsForm.setFieldsValue({
			pb_proxy_url: selectedIntegration.pb_proxy_url ?? '',
			kit_tracking_url: selectedIntegration.kit_tracking_url ?? '',
			shop_floor_api_url: selectedIntegration.shop_floor_api_url ?? '',
			shop_floor_api_user: '',
			shop_floor_api_password: '',
			shop_floor_api_token: '',
		});
	}, [selectedIntegration, integrationsForm]);

	const handleSavePrinter = async (values: Record<string, string>) => {
		if (!selectedBuildingId || !selectedBuilding) return;
		try {
			await updatePrinterSettings({
				id: selectedBuildingId,
				printing_service_url: values.printing_service_url ?? '',
				printer_name_delivery: values.printer_name_delivery ?? '',
				printer_name_kitting: values.printer_name_kitting ?? '',
				printer_name_cutting: values.printer_name_cutting ?? '',
				printer_name_received: values.printer_name_received ?? '',
				printer_name_process_tags: values.printer_name_process_tags ?? '',
			}).unwrap();
			notification.success({
				message: `Printer settings saved for ${selectedBuilding.name}`,
			});
		} catch {
			notification.error({ message: 'Failed to save printer settings' });
		}
	};

	const handleSaveIntegrations = async (values: Record<string, string>) => {
		if (!selectedIntegration) return;

		// Only send secret fields when the user actually typed a replacement.
		const payload: BuildingIntegrationSettingsUpdate = {
			pb_proxy_url: values.pb_proxy_url ?? '',
			kit_tracking_url: values.kit_tracking_url ?? '',
			shop_floor_api_url: values.shop_floor_api_url ?? '',
		};
		if (values.shop_floor_api_user) {
			payload.shop_floor_api_user = values.shop_floor_api_user;
		}
		if (values.shop_floor_api_password) {
			payload.shop_floor_api_password = values.shop_floor_api_password;
		}
		if (values.shop_floor_api_token) {
			payload.shop_floor_api_token = values.shop_floor_api_token;
		}

		try {
			await updateIntegrations({
				id: selectedIntegration.id,
				...payload,
			}).unwrap();
			integrationsForm.setFieldsValue({
				shop_floor_api_user: '',
				shop_floor_api_password: '',
				shop_floor_api_token: '',
			});
			notification.success({
				message: `Integrations saved for ${selectedIntegration.building_name}`,
			});
		} catch {
			notification.error({ message: 'Failed to save integrations' });
		}
	};

	const buildingSelector = (
		<Select
			value={selectedBuildingId}
			onChange={setSelectedBuildingId}
			size="large"
			loading={isBuildingsLoading}
			suffixIcon={<EnvironmentOutlined />}
			style={{ minWidth: 220 }}
			options={buildings.map((b) => ({ label: b.name, value: b.id }))}
		/>
	);

	return (
		<BasePageLayout title="Settings">
			<Space direction="vertical" size="large" style={{ width: '100%' }}>
				{/* Not per building: one catalog for the shop. */}
				<ProcessCatalogCard />

				<BallooningSettingsCard />

				<Space align="center" size="middle">
					<Text strong>Site</Text>
					{buildingSelector}
				</Space>

				<Card title="Printer Settings">
					{isBuildingsLoading ? (
						<Skeleton active />
					) : !selectedBuilding ? (
						<Text type="secondary">No building selected.</Text>
					) : (
						<Form form={printerForm} layout="vertical" onFinish={handleSavePrinter}>
							<Form.Item
								name={SERVICE_URL_FIELD.key}
								label={SERVICE_URL_FIELD.label}
								tooltip="Base URL of the printing microservice running in this building (e.g. http://192.168.1.10:8787)"
							>
								<Input placeholder="e.g. http://192.168.1.10:8787" allowClear />
							</Form.Item>

							<Row gutter={[24, 0]}>
								{PRINTER_FIELDS.map(({ key, label }) => (
									<Col xs={24} sm={12} key={key}>
										<Form.Item
											name={key}
											label={`${label} Printer`}
											tooltip={
												key === 'printer_name_process_tags'
													? PROCESS_TAGS_TOOLTIP
													: `CUPS printer name used for ${label.toLowerCase()} labels in this building`
											}
										>
											<Input
												placeholder={`e.g. Zebra_ZD420_${label}`}
												allowClear
											/>
										</Form.Item>
									</Col>
								))}
							</Row>

							<Form.Item style={{ marginBottom: 0 }}>
								<Button type="primary" htmlType="submit" loading={isSavingPrinter}>
									Save
								</Button>
							</Form.Item>
						</Form>
					)}
				</Card>

				<Card title="Integrations">
					{isIntegrationsLoading ? (
						<Skeleton active />
					) : !selectedIntegration ? (
						<Alert
							type="info"
							showIcon
							message="No integration settings row exists for this building yet. Run the seeding migration or create one via the admin."
						/>
					) : (
						<Form
							form={integrationsForm}
							layout="vertical"
							onFinish={handleSaveIntegrations}
						>
							<Paragraph type="secondary" style={{ marginTop: 0 }}>
								Per-building overrides for on-prem services. Leave a field blank to
								fall back to the global <code>settings.py</code> value. Secret
								fields are encrypted at rest; leave them empty to keep the current
								stored value.
							</Paragraph>

							<Divider orientation="left" plain>
								T150 Proxy
							</Divider>
							<Form.Item
								name="pb_proxy_url"
								label="PB_PROXY URL"
								tooltip="Netloc of the on-prem T150 proxy (e.g. 't150ds:81/'). Used to reach machines, Keyence devices and shop-floor services."
							>
								<Input placeholder="e.g. t150ds:81/" allowClear />
							</Form.Item>

							<Divider orientation="left" plain>
								Kit Tracking
							</Divider>
							<Form.Item
								name="kit_tracking_url"
								label="Kit Tracking URL"
							>
								<Input placeholder="e.g. http://kit-tracking.local/" allowClear />
							</Form.Item>

							<Divider orientation="left" plain>
								Shop Floor App
							</Divider>
							<Form.Item name="shop_floor_api_url" label="API URL">
								<Input placeholder="e.g. http://10.3.3.126:8000/" allowClear />
							</Form.Item>

							<Row gutter={[24, 0]}>
								<Col xs={24} sm={8}>
									<Form.Item
										name="shop_floor_api_user"
										label={
											<Space>
												User
												{selectedIntegration.has_shop_floor_api_user && (
													<Tag color="green">stored</Tag>
												)}
											</Space>
										}
										tooltip="Leave blank to keep the currently stored value."
									>
										<Input.Password
											placeholder={
												selectedIntegration.has_shop_floor_api_user
													? SECRET_PLACEHOLDER
													: ''
											}
											autoComplete="off"
											allowClear
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={8}>
									<Form.Item
										name="shop_floor_api_password"
										label={
											<Space>
												Password
												{selectedIntegration.has_shop_floor_api_password && (
													<Tag color="green">stored</Tag>
												)}
											</Space>
										}
										tooltip="Leave blank to keep the currently stored value."
									>
										<Input.Password
											placeholder={
												selectedIntegration.has_shop_floor_api_password
													? SECRET_PLACEHOLDER
													: ''
											}
											autoComplete="new-password"
											allowClear
										/>
									</Form.Item>
								</Col>
								<Col xs={24} sm={8}>
									<Form.Item
										name="shop_floor_api_token"
										label={
											<Space>
												Token
												{selectedIntegration.has_shop_floor_api_token && (
													<Tag color="green">stored</Tag>
												)}
											</Space>
										}
										tooltip="Leave blank to keep the currently stored value."
									>
										<Input.Password
											placeholder={
												selectedIntegration.has_shop_floor_api_token
													? SECRET_PLACEHOLDER
													: ''
											}
											autoComplete="off"
											allowClear
										/>
									</Form.Item>
								</Col>
							</Row>

							<Form.Item style={{ marginBottom: 0 }}>
								<Button
									type="primary"
									htmlType="submit"
									loading={isSavingIntegrations}
								>
									Save
								</Button>
							</Form.Item>
						</Form>
					)}
				</Card>
			</Space>
		</BasePageLayout>
	);
};

export default SettingsPage;
